import { useEffect, useRef, useState } from 'react';
import './Serviceshowcase.css';
import service2 from '../../assets/service2.png';
import service4 from '../../assets/service4.png';
import service5 from '../../assets/service5.png';
import service6 from '../../assets/service6.png';
import service7 from '../../assets/service7.png';
import service8 from '../../assets/service8.png';
import service3 from '../../assets/service3.mp4';

/* ── Scroll-reveal hook ── */
function useInView(threshold = 0.18) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

/* ── Smart Video ──
   Behaviour:
   • Visible on screen  → unmute automatically (sound plays)
   • Out of screen      → mute immediately AND reset internal state
                          so next visit starts unmuted again
   • Mute button        → lets user silence while section is visible;
                          that choice is wiped the moment they scroll away
── */
function SmartVideo({ src, className }) {
  const videoRef  = useRef(null);
  const wrapperRef = useRef(null);

  // true  = currently muted (either off-screen or user pressed mute)
  // false = sound is on (section is visible and not manually muted)
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Section entered view → unmute & play sound
          video.muted = false;
          setMuted(false);
        } else {
          // Section left view → mute AND reset so next visit is unmuted
          video.muted = true;
          setMuted(true);          // resets button icon back to "unmuted" state
        }
      },
      { threshold: 0.5 }           // triggers when ≥50% of video wrapper visible
    );

    if (wrapperRef.current) obs.observe(wrapperRef.current);
    return () => obs.disconnect();
  }, []);

  // Manual toggle — only meaningful while section is on screen
  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    const next = !muted;
    video.muted = next;
    setMuted(next);
  };

  return (
    <div className="video-wrapper" ref={wrapperRef}>
      <video
        ref={videoRef}
        className={className}
        src={src}
        autoPlay
        loop
        playsInline
        muted                      // start muted — IntersectionObserver will unmute on entry
      />
      <button
        className="video-mute-btn"
        onClick={toggleMute}
        aria-label={muted ? 'Unmute video' : 'Mute video'}
      >
        {muted ? (
          /* Speaker with X — muted */
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
            <line x1="23" y1="9" x2="17" y2="15" />
            <line x1="17" y1="9" x2="23" y2="15" />
          </svg>
        ) : (
          /* Speaker with waves — sound on */
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
            <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
          </svg>
        )}
      </button>
    </div>
  );
}

/* ── Service data ── */
const SERVICES = [
  {
    id: 'wedding',
    tag: 'Wedding Photography',
    heading: 'Turning wedding moments into timeless memories.',
    desc: 'From the first glance to the last dance — we document your wedding with honesty, artistry, and a quiet eye for the moments that define a life together.',
    image: service2,
    imagePos: 'right',
    accent: '#c4a46b',
    bg: '#0e0c09',
    theme: 'golden',
  },
  {
    id: 'video',
    tag: 'Cinematic Videography',
    heading: 'Cinematic storytelling crafted with emotion and elegance.',
    desc: 'Shot on Sony Alpha cameras, colour-graded for feeling. Every film we create is a cinematic keepsake — scored, graded, and built to be watched across all the years ahead.',
    video: service3,
    imagePos: 'left',
    accent: '#c8c8c8',
    bg: '#080808',
    theme: 'film',
  },
  {
    id: 'newborn',
    tag: 'Baby & Newborn',
    heading: 'Tiny moments captured with love and care.',
    desc: 'The smallest fingers, the softest light, the purest quiet. We photograph new life with gentleness and intention — images your family will treasure across generations.',
    image: service4,
    imagePos: 'right',
    accent: '#c4908a',
    bg: '#f5ede8',
    theme: 'soft',
  },
  {
    id: 'maternity',
    tag: 'Maternity Photography',
    heading: 'Celebrating the beauty of motherhood.',
    desc: 'Elegant, warm, and deeply personal. We celebrate the journey of becoming a mother — with imagery that is minimal, timeless, and full of quiet radiance.',
    image: service5,
    imagePos: 'left',
    accent: '#c4a882',
    bg: '#0d0b09',
    theme: 'warm',
  },
  {
    id: 'prewedding',
    tag: 'Pre-Wedding Shoot',
    heading: 'Love stories captured before the big day begins.',
    desc: 'A relaxed, concept-driven session at a place that feels like yours. No staging, no pressure — just two people and the light they love, preserved honestly.',
    image: service6,
    imagePos: 'right',
    accent: '#8db8a4',
    bg: '#08100d',
    theme: 'outdoor',
  },
  {
    id: 'family',
    tag: 'Family Photography',
    heading: 'Framing togetherness and joyful memories.',
    desc: 'The in-between moments are always the real ones. We capture families as they truly are — imperfect, full of laughter, and overflowing with love.',
    image: service7,
    imagePos: 'left',
    accent: '#d4a86a',
    bg: '#0d0a07',
    theme: 'warm',
  },
  {
    id: 'corporate',
    tag: 'Corporate Events',
    heading: 'Professional event coverage with creative precision.',
    desc: 'Every keynote, every milestone, every handshake that matters. We bring the same creative intent to corporate events that we bring to every other story we tell.',
    image: service8,
    imagePos: 'right',
    accent: '#7e9fc2',
    bg: '#07090e',
    theme: 'pro',
  },
  {
    id: 'corporate',
    tag: 'Corporate Events',
    heading: 'Professional event coverage with creative precision.',
    desc: 'Every keynote, every milestone, every handshake that matters. We bring the same creative intent to corporate events that we bring to every other story we tell.',
    image: service8,
    imagePos: 'right',
    accent: '#7e9fc2',
    bg: '#07090e',
    theme: 'pro',
  },
];

/* ── Single service row ── */
function ServiceRow({ svc }) {
  const [ref, inView] = useInView(0.15);

  return (
    <div
      ref={ref}
      className={[
        'svc-row',
        `img-${svc.imagePos}`,
        `svc-row--${svc.theme}`,
        inView ? 'in-view' : '',
      ].join(' ')}
      style={{ '--accent': svc.accent, '--row-bg': svc.bg }}
    >
      <div className="svc-row-grain" aria-hidden="true" />

      {/* ── IMAGE / VIDEO SIDE ── */}
      <div className="svc-row-img-side">
        <div className="svc-image-frame">
          {svc.video ? (
            <SmartVideo src={svc.video} className="svc-row-img" />
          ) : (
            <img
              src={svc.image}
              alt={svc.tag}
              className="svc-row-img"
              loading="lazy"
            />
          )}
        </div>
      </div>

      {/* ── CONTENT SIDE ── */}
      <div className="svc-row-content">
        <div className="svc-row-inner">
          <span className="svc-row-tag">{svc.tag}</span>
          <h2 className="svc-row-heading">{svc.heading}</h2>
          <p className="svc-row-desc">{svc.desc}</p>
          <div className="svc-row-divider" aria-hidden="true" />
     
<a href="portfolio" className="svc-row-btn">
  <span>View Portfolio</span>
</a>
        </div>
      </div>
    </div>
  );
}

/* ── Main export ── */
export default function ServicesShowcase() {
  return (
    <section id="services" className="svc-showcase">
      {SERVICES.map(svc => (
        <ServiceRow key={svc.id} svc={svc} />
      ))}
    </section>
  );
}